// src/App.jsx
import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import "./App.css";
import NavBar from "./components/NavBar";
import { SnackbarProvider } from "notistack";
import Loader from "./components/Loader";
import ProtectedRoute from "./components/ProtectedRoute";
import WishlistPage from "./components/WishlistPage";
import WhatsAppButton from "./components/WhatsAppButton";
import Footer from "./components/Footer";
import SEO from "./components/SEO";
import { useTranslation } from "react-i18next";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { SellerProvider } from "./Context/SellerContext";

const Home = lazy(() => import("./components/Home"));
const Login = lazy(() => import("./components/Login"));
const ForgetPassword = lazy(() => import("./components/ForgetPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const SingUp = lazy(() => import("./components/SingUp"));
const OTPVerification = lazy(() => import("./components/OTPVerification"));
const CategoriesHome = lazy(() => import("./components/CategoriesHome"));
const SubCategories = lazy(() => import("./components/SubCategories"));
const MyCart = lazy(() => import("./components/MyCart"));
const ProductDetails = lazy(() => import("./components/ProductDetails"));
const MyOrders = lazy(() => import("./components/MyOrders"));
const AllProuductes = lazy(() => import("./components/AllProuductes"));
const Account = lazy(() => import("./components/Account"));
const Seller = lazy(() => import("./components/Seller"));

const Layout = ({ children, direction }) => {
  const location = useLocation();
  const hideNavbarPaths = ["/login", "/signup", "/forget-password", "/reset-password", "/otp-verification"];

  return (
    <>
      {!hideNavbarPaths.includes(location.pathname) && <NavBar />}
      <div style={{ direction: direction }}>{children}</div>
      {!hideNavbarPaths.includes(location.pathname) && <Footer />}
    </>
  );
};

const AppContent = ({ direction }) => {
  const location = useLocation();

  const hideWhatsAppButton =
    location.pathname.startsWith("/products/") ||
    location.pathname.startsWith("/product-details/");

  return (
    <div className="App">
      <SEO />
      <Suspense fallback={<Loader />}>
        <Layout direction={direction}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forget-password" element={<ForgetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup" element={<SingUp />} />
            <Route path="/otp-verification" element={<OTPVerification />} />
            <Route path="/" element={<Home />} />
            <Route path="/categories" element={<CategoriesHome />} />
            <Route path="/subcategories" element={<SubCategories />} />
            <Route path="/product-details/:id" element={<ProductDetails />} />
            <Route path="/seller" element={<Seller />} />
            <Route path="/products/:type" element={<AllProuductes />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/my-cart" element={<MyCart />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/wishlist-page" element={<WishlistPage />} />
              <Route path="/account" element={<Account />} />
            </Route>
          </Routes>
        </Layout>
        {!hideWhatsAppButton && <WhatsAppButton />}
      </Suspense>
    </div>
  );
};

function App() {
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState("ltr");

  useEffect(() => {
    setDirection(i18n.language === "ar" ? "rtl" : "ltr");
  }, [i18n.language]);

  return (
    <SellerProvider>
      <Router>
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={3000}
          preventDuplicate
          style={{
            fontFamily: "Josefin Sans, sans-serif",
          }}
        >
          <AppContent direction={direction} />
        </SnackbarProvider>
      </Router>
    </SellerProvider>
  );
}

export default App;
